import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { calculateBookingPrice, PricingRule, SpecialPriceDate } from "@/lib/pricing";
import { format, differenceInMinutes } from "date-fns";
import { sv } from "date-fns/locale";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Calendar,
  Lock,
  Repeat,
  X,
  ArrowLeft,
} from "lucide-react";

import OpenMatchIcon from "@/components/icons/OpenMatchIcon";
import BookingSheetSharedFields from "./booking-sheet/BookingSheetSharedFields";
import BookingSheetCourtForm from "./booking-sheet/BookingSheetCourtForm";
import BookingSheetOpenMatchForm, { type RecurringConfig } from "./booking-sheet/BookingSheetOpenMatchForm";
import BookingSheetTournamentForm from "./booking-sheet/BookingSheetTournamentForm";
import BookingSheetTrainingForm from "./booking-sheet/BookingSheetTrainingForm";
import BookingSheetBlockForm from "./booking-sheet/BookingSheetBlockForm";
import BookingSheetRecurringBlockForm from "./booking-sheet/BookingSheetRecurringBlockForm";
import BookingSheetSubscriptionForm from "./booking-sheet/BookingSheetSubscriptionForm";
import type { Tables } from "@/integrations/supabase/types";

type CourtRow = Tables<"courts">;

interface BookingConfirmData {
  type: string;
  customerUserId?: string;
  finalPrice?: number;
  standardPrice?: number;
  customPrice?: number | null;
  customPriceNote?: string;
  comment?: string;
  description?: string;
  reason?: string;
  [key: string]: unknown;
}

interface AppliedVoucher {
  id: string;
  code: string;
  discount_type: string;
  discount_value: number;
}

type ActivityType = "court" | "open_match" | "tournament" | "training" | "block" | "recurring_block" | "subscription";

interface ActivityOption {
  type: ActivityType;
  icon: React.ReactNode;
  label: string;
  description: string;
  primary?: boolean;
}

// These are populated via t() in the component body below

interface AdminBookingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courtIds: string[];
  courtNames: string[];
  startTime: Date | null;
  endTime: Date | null;
  courts: CourtRow[];
  organizationId: string | null;
  hallId: string | null;
  pricingRules?: PricingRule[];
  specialDates?: SpecialPriceDate[];
  onConfirm: (data: BookingConfirmData) => void;
  isBooking?: boolean;
  onHighlightCourts?: (courtIds: string[]) => void;
  selectedDate?: Date;
}

export default function AdminBookingSheet({
  open,
  onOpenChange,
  courtIds,
  courtNames,
  startTime,
  endTime,
  courts,
  organizationId,
  hallId,
  pricingRules = [],
  specialDates = [],
  onConfirm,
  isBooking = false,
  onHighlightCourts,
  selectedDate,
}: AdminBookingSheetProps) {
  const { t } = useTranslation("admin");
  const { paymentMethodOptions: paymentMethods } = usePaymentMethods();
  const [activityType, setActivityType] = useState<ActivityType | null>(null);

  const ACTIVITY_OPTIONS: ActivityOption[] = [
    { type: "court", icon: <Calendar className="h-5 w-5" />, label: t("bookingSheet.bookCourt"), description: t("bookingSheet.bookCourtDesc"), primary: true },
    { type: "open_match", icon: <OpenMatchIcon className="h-5 w-5" />, label: t("bookingSheet.activity"), description: t("bookingSheet.activityDesc"), primary: true },
    // T2v2/G-022 (decision 4a): Turnering/Träning HIDDEN — their submit ended in
    // a "kommer snart!" toast (full forms, no handler branch). They return with
    // the post-blitz admin wizard (templates/mass-creation — see todo chip).
    // Types + forms are kept so the option rows can simply be re-added.
  ];

  const SECONDARY_OPTIONS: ActivityOption[] = [
    { type: "block", icon: <Lock className="h-4 w-4" />, label: t("bookingSheet.blockTime"), description: t("bookingSheet.blockTimeDesc") },
    { type: "recurring_block", icon: <Repeat className="h-4 w-4" />, label: t("bookingSheet.recurringBlock"), description: t("bookingSheet.recurringBlockDesc") },
    { type: "subscription", icon: <Repeat className="h-4 w-4" />, label: t("bookingSheet.subscription"), description: t("bookingSheet.subscriptionDesc") },
  ];

  const ACTION_LABELS: Record<ActivityType, string> = {
    court: t("bookingSheet.actionBookCourt"),
    open_match: t("bookingSheet.actionCreateMatch"),
    tournament: t("bookingSheet.actionCreateTournament"),
    training: t("bookingSheet.actionCreateTraining"),
    block: t("bookingSheet.actionBlock"),
    recurring_block: t("bookingSheet.actionCreateBlock"),
    subscription: t("bookingSheet.actionCreateSubscription"),
  };

  // Shared fields
  const [additionalCourts, setAdditionalCourts] = useState<string[]>([]);
  const [showAdditionalCourts, setShowAdditionalCourts] = useState(false);
  const [overridePrimaryCourtId, setOverridePrimaryCourtId] = useState<string | null>(null);

  const primaryCourtId = overridePrimaryCourtId ?? courtIds[0] ?? null;
  const courtLabel = courtNames.length <= 1 ? (courtNames[0] ?? "") : `${courtNames[0]}–${courtNames[courtNames.length - 1]}`;

  useEffect(() => {
    if (open && courtIds.length > 1) {
      setAdditionalCourts(courtIds.slice(1));
      setShowAdditionalCourts(true);
    }
    // Initialize blockCourtIds from grid selection
    if (open && courtIds.length > 0) {
      setBlockCourtIds(courtIds);
    }
  }, [open, courtIds.join(",")]);

  // Reset open_match type if multiple courts selected
  useEffect(() => {
    if (activityType === "open_match" && courtIds.length > 1) {
      setActivityType(null);
    }
  }, [courtIds.length, activityType]);

  const [createdBy, setCreatedBy] = useState("club");
  const [comment, setComment] = useState("");

  // Court booking fields
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customPrice, setCustomPrice] = useState("");
  const [isCustomPrice, setIsCustomPrice] = useState(false);
  const [customPriceNote, setCustomPriceNote] = useState("");
  const [discount, setDiscount] = useState("");
  const [discountType, setDiscountType] = useState<"sek" | "percent">("sek");
  const [paymentMethod, setPaymentMethod] = useState("unpaid");
  const [sendPaymentLink, setSendPaymentLink] = useState(false);
  const [freeReason, setFreeReason] = useState("");
  const [freeReasonText, setFreeReasonText] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [selectedPunchCardId, setSelectedPunchCardId] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState<AppliedVoucher | null>(null);
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [cancellationHours, setCancellationHours] = useState(24);
  const [ageGroup, setAgeGroup] = useState("all");

  // Activity fields
  const [matchType, setMatchType] = useState("double");
  const [minLevel, setMinLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(10);
  const [requireLevel, setRequireLevel] = useState(false);
  const [autoCancel, setAutoCancel] = useState(false);
  const [autoCancelHours, setAutoCancelHours] = useState(6);
  const [autoCancelMinPlayers, setAutoCancelMinPlayers] = useState(4);
  const [hallDiscountType, setHallDiscountType] = useState<"none" | "percent" | "fixed" | "free">("none");
  const [hallDiscountValue, setHallDiscountValue] = useState(0);
  const [organizerFee, setOrganizerFee] = useState(0);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [playerNames, setPlayerNames] = useState<Record<string, string>>({});
  const [recurring, setRecurring] = useState<RecurringConfig>({ enabled: false, days: [], weeks: 8 });

  // Tournament fields
  const [tournamentFormat, setTournamentFormat] = useState("americano");
  const [tournamentName, setTournamentName] = useState("");
  const [tournamentPlayers, setTournamentPlayers] = useState(8);
  const [tournamentPoints, setTournamentPoints] = useState(24);
  const [tournamentPrice, setTournamentPrice] = useState("");
  const [privateTournament, setPrivateTournament] = useState(false);
  const [membersOnly, setMembersOnly] = useState(false);

  // Training fields
  const [trainingType, setTrainingType] = useState("group");
  const [coachId, setCoachId] = useState("");
  const [trainingPlayers, setTrainingPlayers] = useState(4);
  const [trainingMinLevel, setTrainingMinLevel] = useState(1);
  const [trainingMaxLevel, setTrainingMaxLevel] = useState(10);
  const [trainingRequireLevel, setTrainingRequireLevel] = useState(false);
  const [giveFeedback, setGiveFeedback] = useState(false);
  const [hideTraining, setHideTraining] = useState(false);
  const [trainingTitle, setTrainingTitle] = useState("");

  // Block fields
  const [blockReason, setBlockReason] = useState("maintenance");
  const [blockDescription, setBlockDescription] = useState("");

  // Recurring block fields
  const [blockName, setBlockName] = useState("");
  const [blockFrom, setBlockFrom] = useState(() => startTime ? format(startTime, "yyyy-MM-dd") : "");
  const [blockTo, setBlockTo] = useState("");
  const [blockDays, setBlockDays] = useState<number[]>([]);
  const [blockInterval, setBlockInterval] = useState("1");
  const [blockPrice, setBlockPrice] = useState("");
  const [blockCustomerId, setBlockCustomerId] = useState<string | null>(null);
  const [blockColor, setBlockColor] = useState("#64748B");
  const [blockComment, setBlockComment] = useState("");
  const [blockCourtIds, setBlockCourtIds] = useState<string[]>([]);
  const [blockCategory, setBlockCategory] = useState("revenue");
  const [blockEstimatedValue, setBlockEstimatedValue] = useState("");
  const [blockIsConfirmedRevenue, setBlockIsConfirmedRevenue] = useState(false);

  // Sync recurring block start date and day-of-week from selected time or schedule date
  useEffect(() => {
    const dateSource = startTime ?? selectedDate;
    if (dateSource) {
      setBlockFrom(format(dateSource, "yyyy-MM-dd"));
      setBlockDays([dateSource.getDay()]);
    }
  }, [startTime, selectedDate]);

  // Sync blockCourtIds from primary + additional courts selection
  useEffect(() => {
    if (activityType === "recurring_block") {
      const allCourts = primaryCourtId ? [primaryCourtId, ...additionalCourts] : [...additionalCourts];
      if (allCourts.length > 0) setBlockCourtIds(allCourts);
    }
  }, [activityType, primaryCourtId, additionalCourts]);

  // Sync court highlighting with grid for recurring blocks
  useEffect(() => {
    if (activityType === "recurring_block" && onHighlightCourts) {
      onHighlightCourts(blockCourtIds);
    }
  }, [activityType, blockCourtIds, onHighlightCourts]);

  // Clear highlighting when sheet closes
  useEffect(() => {
    if (!open && onHighlightCourts) onHighlightCourts([]);
  }, [open, onHighlightCourts]);

  // Subscription fields
  const [subCustomerSearch, setSubCustomerSearch] = useState("");
  const [subCustomerId, setSubCustomerId] = useState<string | null>(null);
  const [subFrom, setSubFrom] = useState(() => startTime ? format(startTime, "yyyy-MM-dd") : "");
  const [subTo, setSubTo] = useState("");
  const [subDays, setSubDays] = useState<number[]>([]);
  const [subInterval, setSubInterval] = useState("1");
  const [subPrice, setSubPrice] = useState("");
  const [subComment, setSubComment] = useState("");

  // Sync subscription start date and day-of-week from selected time or schedule date
  useEffect(() => {
    const dateSource = startTime ?? selectedDate;
    if (dateSource) {
      setSubFrom(format(dateSource, "yyyy-MM-dd"));
      setSubDays([dateSource.getDay()]);
    }
  }, [startTime, selectedDate]);

  // Auto-calculated price (multiplied by court count)
  const totalCourtCount = 1 + additionalCourts.length;
  const { autoPricePerCourt, autoPrice } = useMemo(() => {
    if (!hallId || !startTime || !endTime || pricingRules.length === 0) return { autoPricePerCourt: null, autoPrice: null };
    const durationMin = differenceInMinutes(endTime, startTime);
    if (durationMin <= 0) return { autoPricePerCourt: null, autoPrice: null };
    const result = calculateBookingPrice(pricingRules, hallId, startTime, durationMin);
    const perCourt = result.total > 0 ? result.total : null;
    const total = perCourt != null ? perCourt * totalCourtCount : null;
    return { autoPricePerCourt: perCourt, autoPrice: total };
  }, [hallId, startTime, endTime, pricingRules, totalCourtCount]);

  const activePrice = isCustomPrice && customPrice ? Number(customPrice) : (autoPrice ?? 0);
  const discountedPrice = useMemo(() => {
    if (!discount || Number(discount) <= 0) return activePrice;
    if (discountType === "percent") return Math.round(activePrice * (1 - Number(discount) / 100));
    return Math.max(0, activePrice - Number(discount));
  }, [activePrice, discount, discountType]);

  // Queries
  const { data: customers = [] } = useQuery({
    queryKey: ["admin-booking-sheet-customers", organizationId, customerSearch],
    queryFn: async () => {
      let query = supabase
        .from("customers")
        .select("*")
        .eq("organization_id", organizationId!)
        .limit(10);
      if (customerSearch) {
        query = query.or(`full_name.ilike.%${customerSearch}%,email.ilike.%${customerSearch}%,phone.ilike.%${customerSearch}%`);
      }
      const { data } = await query;
      return data ?? [];
    },
    enabled: !!organizationId && open && !!activityType && customerSearch.length >= 2,
  });

  const { data: profileMatches = [] } = useQuery({
    queryKey: ["admin-booking-sheet-profiles", organizationId, customerSearch],
    queryFn: async () => {
      if (!customerSearch || customerSearch.length < 2) return [];
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .ilike("full_name", `%${customerSearch}%`)
        .limit(5);
      return data ?? [];
    },
    enabled: !!organizationId && open && !!activityType && customerSearch.length >= 2,
  });

  const { data: subCustomers = [] } = useQuery({
    queryKey: ["admin-booking-sheet-sub-customers", organizationId, subCustomerSearch],
    queryFn: async () => {
      let query = supabase
        .from("customers")
        .select("*")
        .eq("organization_id", organizationId!)
        .limit(10);
      if (subCustomerSearch) {
        query = query.or(`full_name.ilike.%${subCustomerSearch}%,email.ilike.%${subCustomerSearch}%,phone.ilike.%${subCustomerSearch}%`);
      }
      const { data } = await query;
      return data ?? [];
    },
    enabled: !!organizationId && open && activityType === "subscription" && (subCustomerSearch as string).length >= 2,
  });

  // Fetch customer punch cards for klippkort payment
  const customerUserId = customers.find((c: any) => c.id === selectedCustomerId)?.user_id as string | undefined;
  const { data: customerPunchCards = [], isLoading: punchCardsLoading } = useQuery({
    queryKey: ["booking-sheet-punch-cards", customerUserId, organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("punch_cards" as any)
        .select("id, name, total_punches, used_punches, expires_at")
        .eq("user_id", customerUserId!)
        .eq("organization_id", organizationId!)
        .eq("status", "active");
      return (data ?? []).map((c: any) => ({
        id: c.id,
        name: c.name,
        remaining: (c.total_punches ?? 0) - (c.used_punches ?? 0),
        expires_at: c.expires_at,
      })).filter((c: any) => c.remaining > 0);
    },
    enabled: !!customerUserId && !!organizationId && open,
  });

  // Fetch hall settings for free booking config
  const { data: hallData } = useQuery({
    queryKey: ["booking-sheet-hall", hallId],
    queryFn: async () => {
      const { data } = await supabase.from("halls").select("free_booking_reasons, allow_free_bookings").eq("id", hallId!).single();
      return data;
    },
    enabled: !!hallId && open,
  });
  const freeBookingReasons = (hallData?.free_booking_reasons as string[] | null) ?? ["Personal", "Underhåll", "Event", "Annat"];
  const allowFreeBookings = (hallData?.allow_free_bookings as boolean | null) ?? true;

  const { data: coaches = [] } = useQuery({
    queryKey: ["admin-coaches", organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("organization_members")
        .select("user_id, profiles:user_id(full_name)")
        .eq("organization_id", organizationId!)
        .eq("role", "coach");
      return data ?? [];
    },
    enabled: !!organizationId && open && activityType === "training",
  });

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  // Generic field change handler
   
  const handleFieldChange = (field: string, value: unknown) => {
    const setters: Record<string, (v: any) => void> = {
      additionalCourts: setAdditionalCourts,
      showAdditionalCourts: setShowAdditionalCourts,
      primaryCourtId: setOverridePrimaryCourtId,
      createdBy: setCreatedBy,
      customerSearch: setCustomerSearch,
      customPrice: setCustomPrice,
      isCustomPrice: setIsCustomPrice,
      customPriceNote: setCustomPriceNote,
      discount: setDiscount,
      discountType: setDiscountType,
      paymentMethod: setPaymentMethod,
      sendPaymentLink: setSendPaymentLink,
      freeReason: setFreeReason,
      freeReasonText: setFreeReasonText,
      paymentReference: setPaymentReference,
      selectedPunchCardId: setSelectedPunchCardId,
      voucherCode: setVoucherCode,
      appliedVoucher: setAppliedVoucher,
      notifyCustomer: setNotifyCustomer,
      cancellationHours: setCancellationHours,
      ageGroup: setAgeGroup,
      matchType: setMatchType,
      minLevel: setMinLevel,
      maxLevel: setMaxLevel,
      requireLevel: setRequireLevel,
      autoCancel: setAutoCancel,
      autoCancelHours: setAutoCancelHours,
      autoCancelMinPlayers: setAutoCancelMinPlayers,
      tournamentFormat: setTournamentFormat,
      tournamentName: setTournamentName,
      tournamentPlayers: setTournamentPlayers,
      tournamentPoints: setTournamentPoints,
      tournamentPrice: setTournamentPrice,
      privateTournament: setPrivateTournament,
      membersOnly: setMembersOnly,
      trainingType: setTrainingType,
      coachId: setCoachId,
      trainingPlayers: setTrainingPlayers,
      trainingMinLevel: setTrainingMinLevel,
      trainingMaxLevel: setTrainingMaxLevel,
      trainingRequireLevel: setTrainingRequireLevel,
      giveFeedback: setGiveFeedback,
      hideTraining: setHideTraining,
      trainingTitle: setTrainingTitle,
      blockReason: setBlockReason,
      blockDescription: setBlockDescription,
      blockName: setBlockName,
      blockFrom: setBlockFrom,
      blockTo: setBlockTo,
      blockDays: setBlockDays,
      blockInterval: setBlockInterval,
      blockPrice: setBlockPrice,
      blockCustomerId: setBlockCustomerId,
      blockColor: setBlockColor,
      blockComment: setBlockComment,
      blockCourtIds: setBlockCourtIds,
      blockCategory: setBlockCategory,
      blockEstimatedValue: setBlockEstimatedValue,
      blockIsConfirmedRevenue: setBlockIsConfirmedRevenue,
      subCustomerSearch: setSubCustomerSearch,
      subCustomerId: setSubCustomerId,
      subFrom: setSubFrom,
      subTo: setSubTo,
      subDays: setSubDays,
      subInterval: setSubInterval,
      subPrice: setSubPrice,
      subComment: setSubComment,
    };
    setters[field]?.(value);
  };

  const formState: Record<string, unknown> = {
    additionalCourts, showAdditionalCourts, createdBy,
    isCustomPrice, customPrice, customPriceNote, discount, discountType,
    paymentMethod, sendPaymentLink, freeReason, freeReasonText, paymentReference, selectedPunchCardId, voucherCode, appliedVoucher, notifyCustomer, cancellationHours, ageGroup,
    matchType, minLevel, maxLevel, requireLevel, autoCancel, autoCancelHours, autoCancelMinPlayers,
    tournamentFormat, tournamentName, tournamentPlayers, tournamentPoints, tournamentPrice, privateTournament, membersOnly,
    trainingType, coachId, trainingPlayers, trainingMinLevel, trainingMaxLevel, trainingRequireLevel, giveFeedback, hideTraining, trainingTitle,
    blockReason, blockDescription,
    blockName, blockFrom, blockTo, blockDays, blockInterval, blockPrice, blockCustomerId, blockColor, blockComment, blockCourtIds,
    blockCategory, blockEstimatedValue, blockIsConfirmedRevenue,
    subCustomerSearch, subFrom, subTo, subDays, subInterval, subPrice, subComment,
  };

  const handleConfirm = () => {
    if (!activityType) return;
    const base = {
      type: activityType,
      courtId: primaryCourtId,
      additionalCourts,
      startTime,
      endTime,
      createdBy,
      comment,
    };

    switch (activityType) {
      case "court":
        onConfirm({
          ...base,
          customerId: selectedCustomerId,
          customerUserId: selectedCustomer?.user_id,
          customPrice: isCustomPrice ? Number(customPrice) : null,
          customPriceNote: isCustomPrice ? customPriceNote || null : null,
          autoPrice,
          finalPrice: discountedPrice,
          standardPrice: autoPrice ?? 0,
          discount: discount ? Number(discount) : 0,
          discountType,
          paymentMethod,
          sendPaymentLink,
          freeReason: freeReason || undefined,
          freeReasonText: freeReasonText || undefined,
          selectedPunchCardId: selectedPunchCardId || (customerPunchCards.length === 1 ? customerPunchCards[0].id : undefined),
          notifyCustomer,
          cancellationHours,
          ageGroup,
        });
        break;
      case "open_match":
        onConfirm({
          ...base,
          matchType, minLevel, maxLevel, requireLevel,
          hallDiscountType, hallDiscountValue, organizerFee,
          autoCancel, autoCancelHours, autoCancelMinPlayers,
          selectedPlayers,
          recurring,
        });
        break;
      case "tournament":
        onConfirm({
          ...base,
          format: tournamentFormat, name: tournamentName,
          maxPlayers: tournamentPlayers, pointsPerMatch: tournamentPoints,
          pricePerPlayer: tournamentPrice ? Number(tournamentPrice) : 0,
          paymentMethod, isPrivate: privateTournament, membersOnly,
        });
        break;
      case "training":
        onConfirm({
          ...base,
          trainingType, coachId, maxPlayers: trainingPlayers,
          minLevel: trainingMinLevel, maxLevel: trainingMaxLevel,
          requireLevel: trainingRequireLevel, giveFeedback, hideTraining,
          title: trainingTitle, paymentMethod,
        });
        break;
      case "block":
        onConfirm({ ...base, reason: blockReason, description: blockDescription });
        break;
      case "recurring_block":
        onConfirm({
          ...base,
          blockName,
          blockCourtIds: blockCourtIds.length > 0 ? blockCourtIds : courtIds,
          validFrom: blockFrom,
          validUntil: blockTo || undefined,
          daysOfWeek: blockDays,
          intervalWeeks: Number(blockInterval),
          price: blockCategory === "revenue" && blockPrice ? Number(blockPrice) : 0,
          customerId: blockCategory === "revenue" ? blockCustomerId : null,
          color: blockColor,
          blockComment: blockComment || undefined,
          blockCategory,
          estimatedValue: blockCategory === "revenue" && blockEstimatedValue ? Number(blockEstimatedValue) : undefined,
          isConfirmedRevenue: blockCategory === "revenue" ? blockIsConfirmedRevenue : false,
        });
        break;
      case "subscription":
        onConfirm({
          ...base, customerId: subCustomerId,
          validFrom: subFrom, validUntil: subTo, daysOfWeek: subDays,
          intervalWeeks: Number(subInterval), price: subPrice ? Number(subPrice) : 0, paymentMethod,
          subscriptionComment: subComment || undefined,
        });
        break;
    }
  };

  const resetState = () => {
    setActivityType(null);
    setAdditionalCourts([]);
    setShowAdditionalCourts(false);
    setOverridePrimaryCourtId(null);
    setCustomerSearch("");
    setSelectedCustomerId(null);
    setCustomPrice("");
    setIsCustomPrice(false);
    setCustomPriceNote("");
    setDiscount("");
    setComment("");
    setTournamentName("");
    setTrainingTitle("");
    setBlockDescription("");
    setHallDiscountType("none");
    setHallDiscountValue(0);
    setOrganizerFee(0);
    setSelectedPlayers([]);
    setPlayerNames({});
    setRecurring({ enabled: false, days: [], weeks: 8 });
    // Subscription fields
    setSubCustomerSearch("");
    setSubCustomerId(null);
    setSubTo("");
    setSubInterval("1");
    setSubPrice("");
    setSubComment("");
    // Recurring block fields
    setBlockName("");
    setBlockTo("");
    setBlockInterval("1");
    setBlockPrice("");
    setBlockCustomerId(null);
    setBlockColor("#64748B");
    setBlockComment("");
    setBlockCourtIds([]);
    setBlockCategory("revenue");
    setBlockEstimatedValue("");
    setBlockIsConfirmedRevenue(false);
    // Payment
    setPaymentMethod("customer_pays");
  };

  const handleClose = (v: boolean) => {
    if (!v) resetState();
    onOpenChange(v);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose} modal={false}>
      <SheetContent
        side="right"
        className="w-[480px] max-w-full p-0 flex flex-col border-l shadow-xl [&>button.absolute]:hidden"
      >
        {/* Header */}
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border space-y-1">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base">{t("bookingSheet.newActivity")}</SheetTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleClose(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          {startTime && (
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="text-xs font-normal">
                {format(startTime, "EEEE d MMM", { locale: sv })}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {format(startTime, "HH:mm")}–{endTime ? format(endTime, "HH:mm") : ""}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {courtLabel}
              </Badge>
            </div>
          )}
        </SheetHeader>

        {/* Scrollable content */}
        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">
            {/* Step 1: Activity type selection */}
            {!activityType && (
              <div className="space-y-3 animate-fade-in">
                <p className="text-sm font-medium text-foreground">{t("bookingSheet.chooseActivityType")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {ACTIVITY_OPTIONS.map((opt) => {
                    const isMatchMultiCourt = opt.type === "open_match" && courtIds.length > 1;
                    return (
                      <button
                        key={opt.type}
                        onClick={() => !isMatchMultiCourt && setActivityType(opt.type)}
                        disabled={isMatchMultiCourt}
                        className={cn(
                          "flex flex-col items-center gap-1.5 p-4 rounded-lg border-2 transition-all text-center",
                          isMatchMultiCourt
                            ? "border-border opacity-40 cursor-not-allowed"
                            : "border-border hover:border-primary/40 hover:bg-accent/50"
                        )}
                        title={isMatchMultiCourt ? t("bookingSheet.activityRequiresOneCourt") : undefined}
                      >
                        <div className="p-2 rounded-md bg-muted text-muted-foreground">
                          {opt.icon}
                        </div>
                        <span className="text-sm font-medium">{opt.label}</span>
                        <span className="text-[10px] text-muted-foreground leading-tight">
                          {isMatchMultiCourt ? t("bookingSheet.selectOneCourt") : opt.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {SECONDARY_OPTIONS.map((opt) => (
                    <button
                      key={opt.type}
                      onClick={() => { setActivityType(opt.type); if (opt.type === "subscription") setPaymentMethod("invoice"); }}
                      className="flex items-center gap-2 p-3 rounded-lg border-2 border-border hover:border-primary/40 hover:bg-accent/50 transition-all"
                    >
                      <div className="p-1.5 rounded bg-muted text-muted-foreground shrink-0">
                        {opt.icon}
                      </div>
                      <div className="text-left min-w-0">
                        <p className="text-sm font-medium truncate">{opt.label}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{opt.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Form for selected type */}
            {activityType && (
              <div className="space-y-4 animate-fade-in">
                {/* Back link */}
                <button
                  onClick={() => setActivityType(null)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3 w-3" />
                  <span>{t("bookingSheet.changeActivityType")}</span>
                </button>

                {/* Current type indicator */}
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                    {[...ACTIVITY_OPTIONS, ...SECONDARY_OPTIONS].find(o => o.type === activityType)?.icon}
                  </div>
                  <span className="text-sm font-semibold">{ACTION_LABELS[activityType]}</span>
                </div>

                {/* Shared fields */}
                <BookingSheetSharedFields
                  formState={formState}
                  onFieldChange={handleFieldChange}
                  startTime={startTime}
                  endTime={endTime}
                  courtLabel={courtLabel}
                  courts={courts}
                  courtIds={overridePrimaryCourtId ? [overridePrimaryCourtId] : courtIds}
                  comment={comment}
                  onCommentChange={setComment}
                  maxCourts={activityType === "open_match" ? 1 : undefined}
                  hideDate={activityType === "subscription" || activityType === "recurring_block"}
                />

                {/* Activity-specific forms */}
                {activityType === "court" && (
                  <BookingSheetCourtForm
                    formState={formState}
                    onFieldChange={handleFieldChange}
                    customers={customers}
                    selectedCustomerId={selectedCustomerId}
                    onSelectCustomer={(id, name) => {
                      setSelectedCustomerId(id);
                      if (name) setCustomerSearch(name);
                    }}
                    customerSearch={customerSearch}
                    onCustomerSearch={setCustomerSearch}
                    autoPrice={autoPrice}
                    autoPricePerCourt={autoPricePerCourt}
                    totalCourtCount={totalCourtCount}
                    activePrice={activePrice}
                    discountedPrice={discountedPrice}
                    paymentMethods={paymentMethods}
                    organizationId={organizationId}
                    freeBookingReasons={freeBookingReasons}
                    allowFreeBookings={allowFreeBookings}
                    customerPunchCards={customerPunchCards}
                    customerPunchCardsLoading={punchCardsLoading}
                  />
                )}
                {activityType === "open_match" && (
                  <BookingSheetOpenMatchForm
                    formState={formState}
                    onFieldChange={handleFieldChange}
                    pricingRules={pricingRules}
                    hallId={hallId}
                    startTime={startTime}
                    endTime={endTime}
                    courtId={primaryCourtId}
                    specialDates={specialDates}
                    hallDiscountType={hallDiscountType}
                    hallDiscountValue={hallDiscountValue}
                    organizerFee={organizerFee}
                    onHallDiscountTypeChange={setHallDiscountType}
                    onHallDiscountValueChange={setHallDiscountValue}
                    onOrganizerFeeChange={setOrganizerFee}
                    selectedPlayers={selectedPlayers}
                    playerNames={playerNames}
                    onPlayersChange={(players, names) => {
                      setSelectedPlayers(players);
                      setPlayerNames(names);
                    }}
                    recurring={recurring}
                    onRecurringChange={setRecurring}
                  />
                )}
                {activityType === "tournament" && (
                  <BookingSheetTournamentForm formState={formState} onFieldChange={handleFieldChange} paymentMethods={paymentMethods} />
                )}
                {activityType === "training" && (
                  <BookingSheetTrainingForm formState={formState} onFieldChange={handleFieldChange} coaches={coaches} paymentMethods={paymentMethods} />
                )}
                {activityType === "block" && (
                  <BookingSheetBlockForm formState={formState} onFieldChange={handleFieldChange} />
                )}
                {activityType === "recurring_block" && (
                  <BookingSheetRecurringBlockForm
                    formState={formState}
                    onFieldChange={handleFieldChange}
                    courts={courts.map((c: CourtRow) => ({ id: c.id, name: c.name }))}
                    selectedCourtIds={courtIds}
                    customers={subCustomers}
                    selectedCustomerId={blockCustomerId}
                    onSelectCustomer={(id, name) => { setBlockCustomerId(id); if (name) setSubCustomerSearch(name); }}
                    customerSearch={subCustomerSearch}
                    onCustomerSearch={setSubCustomerSearch}
                    organizationId={organizationId}
                    standardPriceHint={autoPricePerCourt != null ? autoPricePerCourt * Math.max(1, blockCourtIds.length || courtIds.length) : null}
                    courtCount={blockCourtIds.length || courtIds.length}
                  />
                )}
                {activityType === "subscription" && (
                  <BookingSheetSubscriptionForm
                    formState={formState}
                    onFieldChange={handleFieldChange}
                    paymentMethods={paymentMethods}
                    customers={subCustomers}
                    selectedCustomerId={subCustomerId}
                    onSelectCustomer={(id, name) => {
                      setSubCustomerId(id);
                      if (name) setSubCustomerSearch(name);
                    }}
                    customerSearch={subCustomerSearch as string}
                    onCustomerSearch={setSubCustomerSearch}
                    organizationId={organizationId}
                    pricingRules={pricingRules}
                    specialDates={specialDates}
                    hallId={hallId}
                    startTime={startTime}
                    endTime={endTime}
                  />
                )}

                {/* Common bottom: comment */}
                <div className="border-t border-border pt-3">
                  <Label className="text-xs">{t("bookingSheet.commentLabel")}</Label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="text-sm mt-1"
                    rows={2}
                    placeholder={t("bookingSheet.commentPlaceholder")}
                    maxLength={2000}
                  />
                  <p className="text-[10px] text-muted-foreground text-right mt-0.5">{comment.length}/2000</p>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Sticky footer */}
        <div className="border-t border-border px-5 py-3 flex items-center justify-end gap-2 bg-background">
          <Button variant="outline" onClick={() => handleClose(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!activityType || isBooking}
            className="min-w-[140px]"
          >
            {activityType ? ACTION_LABELS[activityType] : t("bookingSheet.chooseType")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
